import { Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import type { FormulaDataCustomValues } from "../../ExpressionEditor.types";
import { CustomValueNodeView } from "./CustomValueNodeView";

interface CustomValueInTemplateStorage {
  values: FormulaDataCustomValues;
  focussed: Record<string, boolean>;
  templateId: string | null;
}

declare module "@tiptap/react" {
  interface Storage {
    customValueInTemplate: CustomValueInTemplateStorage;
  }
}

/**
 * Tiptap extension to represent a custom value within a formula template.
 * It starts by showing the custom value's name (similar to the usage of Mention for regular variables),
 * but additionally it can show a focussed state, as well as the actual value to use in the expression.
 * This is accessible via the editor's storage (editor.storage.customValueInTemplate).
 */
const CustomValueInTemplate = Node.create<
  Record<string, never>,
  CustomValueInTemplateStorage
>({
  name: "customValueInTemplate",

  // treat as a single atomic inline element, similar to Mentions
  inline: true,
  atom: true,
  group: "inline",

  addAttributes() {
    return {
      id: { default: null },
      label: { default: null }
    };
  },

  addStorage() {
    return {
      values: {},
      focussed: {},
      // template ID is not node-specific, but still included here to group all template-related stuff
      templateId: null
    };
  },

  onTransaction({ transaction }) {
    // allow storage to be modified from outside via transaction metadata
    const meta = transaction.getMeta("customValueInTemplate");
    if (meta?.templateId !== undefined) {
      this.storage.templateId = meta.templateId;
    }
  },

  renderText({ node }) {
    return node.attrs.label;
  },

  renderHTML() {
    // doesn't need any specifics since rendering details are handled via ReactNodeViewRenderer below
    return ["span"];
  },

  addNodeView() {
    // for dynamic rendering (focus & actual value), we use a dedicated component as node view
    return ReactNodeViewRenderer(CustomValueNodeView);
  }
});

export { CustomValueInTemplate };
